/**
 * CR130 Phase 3: Deal Details State Context
 * 
 * Provides centralized state management for deal details across all modes:
 * - Panel: tracks currently selected deal for inspector
 * - Expand: tracks which deals are expanded (single or multi)
 * - Flip: managed locally per card (no global state needed)
 * 
 * Each list surface (ResultsSection, DealFinderPreview, LivePreview) 
 * should have its own provider instance to avoid cross-contamination.
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import type { Deal } from "@/lib/types/deals";
import type { DetailsMode, DetailsSectionConfig } from "@/lib/layout-studio/dealCardUXTypes";

// ================================================================
// CONTEXT TYPE
// ================================================================

export interface DealDetailsContextValue {
  // Current mode from UX config
  mode: DetailsMode;
  
  // Panel mode state
  selectedDealId: string | null;
  selectedDeal: (Deal & { id?: string }) | null;
  selectDeal: (deal: (Deal & { id?: string }) | null) => void;
  closePanel: () => void;
  
  // Expand mode state
  expandedDealIds: Set<string>;
  singleExpandMode: boolean;
  toggleExpand: (dealId: string) => void;
  isExpanded: (dealId: string) => boolean;
  collapseAll: () => void;
  
  // Sections config
  sectionsConfig: DetailsSectionConfig[];
}

const DealDetailsContext = createContext<DealDetailsContextValue | null>(null);

// ================================================================
// PROVIDER
// ================================================================

interface DealDetailsProviderProps {
  children: ReactNode;
  mode: DetailsMode;
  singleExpandMode?: boolean;
  sectionsConfig: DetailsSectionConfig[];
}

export function DealDetailsProvider({
  children,
  mode,
  singleExpandMode = true,
  sectionsConfig,
}: DealDetailsProviderProps) {
  // Panel mode state
  const [selectedDeal, setSelectedDeal] = useState<(Deal & { id?: string }) | null>(null);
  
  // Expand mode state
  const [expandedDealIds, setExpandedDealIds] = useState<Set<string>>(new Set());
  
  const selectedDealId = selectedDeal?.id || null;
  
  const selectDeal = useCallback((deal: (Deal & { id?: string }) | null) => {
    setSelectedDeal(deal);
  }, []);
  
  const closePanel = useCallback(() => {
    setSelectedDeal(null);
  }, []);
  
  const toggleExpand = useCallback((dealId: string) => {
    setExpandedDealIds(prev => {
      const next = new Set(prev);
      if (next.has(dealId)) {
        next.delete(dealId);
      } else {
        if (singleExpandMode) {
          // Close all others
          next.clear();
        }
        next.add(dealId);
      }
      return next;
    });
  }, [singleExpandMode]);
  
  const isExpanded = useCallback((dealId: string) => {
    return expandedDealIds.has(dealId);
  }, [expandedDealIds]);
  
  const collapseAll = useCallback(() => {
    setExpandedDealIds(new Set());
  }, []);
  
  const value: DealDetailsContextValue = {
    mode,
    selectedDealId,
    selectedDeal,
    selectDeal,
    closePanel,
    expandedDealIds,
    singleExpandMode,
    toggleExpand,
    isExpanded,
    collapseAll,
    sectionsConfig,
  };
  
  return (
    <DealDetailsContext.Provider value={value}>
      {children}
    </DealDetailsContext.Provider>
  );
}

// ================================================================
// HOOK
// ================================================================

export function useDealDetails(): DealDetailsContextValue {
  const context = useContext(DealDetailsContext);
  
  if (!context) {
    throw new Error("useDealDetails must be used within a DealDetailsProvider");
  }
  
  return context;
}

/**
 * Optional hook that returns null if not in a provider context.
 * Useful for components that work both inside and outside of details mode.
 */
export function useDealDetailsOptional(): DealDetailsContextValue | null {
  return useContext(DealDetailsContext);
}
